import * as React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import CheckWithholdingItem from 'src/models/personnel/CheckWithholdingItem';
import { ODataOptions, ODataFilter, ODataFilterOperators } from 'src/util/ODataBuilder';

interface ICheckRegisterWithholdingDialogProps {
    dialogName: string;
    hideDialog: () => void;
}

// Mirrors the Check_Withholding block (opened by the main screen's "Withholding" button) - a
// grid of withholding amounts by period/contractor, sourced from the `check_withholding` table.
// The block has no design-time default WHERE clause, but its four items (PERIOD_NO, TSD_NO,
// DSCR, AMOUNT) are all queryable via Oracle Forms' standard Query menu (Enter Query/Execute
// Query) - these filter inputs + Search button mirror that same capability on the web.
const CheckRegisterWithholdingDialog = (props: ICheckRegisterWithholdingDialogProps) => {
    const { get, response } = useFetch();
    const { register, getValues, reset } = useForm();
    const [rows, setRows] = useState<Array<CheckWithholdingItem>>([]);

    const fetchWithholding = async (filters: ODataFilter[] = []) => {
        // check_withholding has 164,716+ rows - always page this, never fetch unbounded.
        const options = new ODataOptions().addSkipTop(0, 50);
        filters.forEach((filter) => options.addFilter(filter));
        const result = await get(`/v1.0/Payroll/withholding${options.toString()}`);
        if (response.ok) {
            setRows(result?.items ?? []);
        }
    };

    useEffect(() => {
        fetchWithholding();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = () => {
        const values = getValues();
        const filters: ODataFilter[] = [];

        // SQL evaluates WHERE before the SELECT list's aliases exist, so these have to reference
        // the raw check_withholding column names (period_no/tsd_no/dscr/amount), not the PeriodNo/
        // TsdNo/Dscr/Amount aliases used for the returned/display fields.
        if (values.periodNo) {
            filters.push(new ODataFilter('period_no', ODataFilterOperators.Equals, values.periodNo));
        }
        if (values.tsdNo) {
            filters.push(new ODataFilter('tsd_no', ODataFilterOperators.Equals, values.tsdNo));
        }
        if (values.dscr) {
            filters.push(new ODataFilter('dscr', ODataFilterOperators.Contains, values.dscr));
        }
        if (values.amount) {
            filters.push(new ODataFilter('amount', ODataFilterOperators.Equals, values.amount));
        }

        fetchWithholding(filters);
    };

    const handleClear = () => {
        reset({ periodNo: '', tsdNo: '', dscr: '', amount: '' });
        fetchWithholding();
    };

    return (
        <Dialog title="Withholding" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '50%' }}>
            <div className="form-row" style={{ alignItems: 'flex-end' }}>
                <RHFInputBox label="Period No." id="periodNo" name="periodNo" type="number" register={register} bodyStyle={{ width: '22%' }} style={{ width: '100%' }} />
                <RHFInputBox label="DLC No." id="tsdNo" name="tsdNo" type="number" register={register} bodyStyle={{ width: '22%' }} style={{ width: '100%' }} />
                <RHFInputBox label="Description" id="dscr" name="dscr" type="text" register={register} bodyStyle={{ width: '30%' }} style={{ width: '100%' }} />
                <RHFInputBox label="Amount" id="amount" name="amount" type="number" register={register} bodyStyle={{ width: '20%' }} style={{ width: '100%' }} />
            </div>
            <div className="form-row" style={{ marginBottom: '1rem' }}>
                <Button className="blue-hover-button" style={{ marginRight: '.5rem' }} text="Search" onClick={handleSearch} />
                <Button className="blue-hover-button" text="Clear" onClick={handleClear} />
            </div>
            <div style={{ maxHeight: '25rem', overflowY: 'auto' }}>
                <table style={{ width: '100%' }}>
                    <thead>
                        <tr>
                            <th>Period No.</th>
                            <th>DLC No.</th>
                            <th>Description</th>
                            <th>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={index}>
                                <td>{row.periodNo}</td>
                                <td>{row.tsdNo}</td>
                                <td>{row.dscr}</td>
                                <td style={{ textAlign: 'right' }}>{(row.amount ?? 0).toFixed(2)}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={4} className="bold-centered">No Withholding Records</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button className="blue-hover-button" style={{ width: '20%' }} text="OK" onClick={() => props.hideDialog()} />
            </div>
        </Dialog>
    );
};

export default CheckRegisterWithholdingDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/checkRegister/CheckRegisterWithholdingDialog.tsx